import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  FormComponentType, 
  IFormControlChange, 
  IFormValidationErrors
} from './df-form-controls/df-form-preview-interfaces'
// Optional external dependencies - can be injected via props or will be undefined if not available
// These are wrapped in try-catch to allow the package to work without these dependencies
let conditionalLogicService: any;
let toastService: any;

// Optional external dependencies - these will be undefined in package context
// Services can be injected via props or will gracefully degrade
// In package mode, these services are not available and components will work with fallbacks
conditionalLogicService = undefined;
toastService = undefined;

// Type fallback for TFormComponent - can be overridden by consuming application
type TFormComponent = any;

// Import form control components
import { DfFormInput } from './df-form-controls/df-form-input'
import { DfFormTextarea } from './df-form-controls/df-form-textarea'
import { DfFormSelect } from './df-form-controls/df-form-select'
import { DfFormCheckbox } from './df-form-controls/df-form-checkbox'
import { DfFormRadio } from './df-form-controls/df-form-radio'
import { DfFormSegment } from './df-form-controls/df-form-segment'
import { DfFormDateTime } from './df-form-controls/df-form-date-time'
import { DfFormSignature } from './df-form-controls/df-form-signature'
import { DfFormFileUpload } from './df-form-controls/df-form-file-upload'
import { DfFormLocation } from './df-form-controls/df-form-location'
import { DfFormHeading } from './df-form-controls/df-form-heading'
import { DfFormInstruction } from './df-form-controls/df-form-instruction'
import { DfFormSection } from './df-form-controls/df-form-section/df-form-section'
import { DfFormDataGrid } from './df-form-controls/df-form-grid/df-form-grid'
import { ComponentActionFeatures } from './ComponentActionFeatures'

// Import styles
import './DfFormPreview.scss'

// Dynamic imports to avoid circular dependencies
const DfFormTable = React.lazy(() => import('./df-form-controls/df-form-table/df-form-table'))

export type DeviceType = 'desktop' | 'tablet' | 'mobile'

export interface DfFormPreviewProps {
  formComponents: FormComponentType[]
  formData?: any
  currentDevice?: DeviceType
  isPreviewMode?: boolean
  initialFormData?: FormComponentType[]
  onSubmit?: (formData: FormComponentType[]) => void
  onFormDataChange?: (formData: FormComponentType[]) => void
  formTitle?: string
  formDescription?: string
  formTemplateId?: string
  // Optional component management props for edit mode
  onComponentSelect?: (component: FormComponentType) => void
  onComponentDelete?: (component: FormComponentType, event: React.MouseEvent) => void
  onComponentEdit?: (component: FormComponentType) => void
  onComponentUpdate?: (componentId: string, updates: Partial<FormComponentType>) => void
  selectedComponent?: FormComponentType | null
}

export const DfFormPreview: React.FC<DfFormPreviewProps> = ({
  formComponents = [],
  currentDevice = 'desktop',
  isPreviewMode = false,
  initialFormData = [],
  onSubmit,
  onFormDataChange,
  formTitle,
  formDescription,
  formTemplateId,
  // Add component management props for edit mode
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  onComponentUpdate,
  selectedComponent
}) => {
  const { t } = useTranslation()
  
  // Form state
  const [formValues, setFormValues] = useState<Record<string, any>>({})
  const [validationErrors, setValidationErrors] = useState<IFormValidationErrors>({})
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({})
  const [componentVisibility, setComponentVisibility] = useState<Record<string, boolean>>({})
  // Track raised issues for threshold conditions (Set of condition IDs)
  const [raisedThresholdIssues, setRaisedThresholdIssues] = useState<Set<string>>(new Set())
  // Track threshold validation state for submit button
  const [thresholdValidationState, setThresholdValidationState] = useState<{ isValid: boolean; errorMessage?: string }>({ isValid: true })

  // Initialize form state
  useEffect(() => {
    initializeFormState()
  }, [formComponents, initialFormData])

  // Recursive function to initialize values from nested components
  const initializeComponentValues = (components: any[], values: Record<string, any>) => {
    components.forEach(component => {
      if (component.id) {
        // Always initialize component in form state, even with empty values
        // This ensures that value changes are tracked properly
        if ('value' in component.basic && component.basic.value !== undefined) {
          values[component.id] = component.basic.value
        }
        // Fallback to defaultValue if no value
        else if ('defaultValue' in component.basic && component.basic.defaultValue !== undefined) {
          values[component.id] = component.basic.defaultValue
        }
        // If neither value nor defaultValue exists, initialize with appropriate default based on component type
        else {
          // For checkbox and multi-select components, initialize with empty array
          if (component.name === 'checkbox' || component.name === 'select') {
            values[component.id] = []
          } else {
            // For other components, initialize with empty string
            values[component.id] = ''
          }
        }
      }

      // Handle nested components in table cells
      if (component.cells && Array.isArray(component.cells)) {
        component.cells.forEach((row: any[], _rowIndex: number) => {
          row.forEach((cell: any, _cellIndex: number) => {
            if (cell.components && Array.isArray(cell.components)) {
              initializeComponentValues(cell.components, values)
            }
          })
        })
      }

      // Handle nested components in datagrid entries
      if (component.entries && Array.isArray(component.entries)) {
        component.entries.forEach((entry: any, _entryIndex: number) => {
          if (entry.components && Array.isArray(entry.components)) {
            initializeComponentValues(entry.components, values)
          }
        })
      }

      // Handle nested components in section children
      if (component.children && Array.isArray(component.children)) {
        initializeComponentValues(component.children, values)
      }
    })
  }

  const initializeFormState = useCallback(() => {
    const initialValues: Record<string, any> = {}
    
    // Initialize form values from initial data
    if (initialFormData && initialFormData.length > 0) {
      initializeComponentValues(initialFormData, initialValues)
    }
    
    // Initialize form values from form components (for submitted data)
    if (formComponents && formComponents.length > 0) {
      initializeComponentValues(formComponents, initialValues)
    }
    setFormValues(initialValues)

    // Evaluate initial conditional logic
    evaluateConditionalLogic()
  }, [initialFormData, formComponents])

  // Recursive function to evaluate conditional logic for nested components
  const evaluateComponentConditionalLogic = (components: any[], visibility: Record<string, boolean>) => {
    components.forEach(component => {
      if (component.id) {
        // Skip conditional logic for table and datagrid components - they are always visible
        if (component.name === 'table' || component.name === 'datagrid') {
          visibility[component.id] = true
        } else if (component.conditional) {
          // Use the proper conditional logic service
          if (conditionalLogicService?.evaluateConditionalLogic) {
            const result = conditionalLogicService.evaluateConditionalLogic(
              component.conditional,
              formComponents as TFormComponent[],
              formValues
            )
            visibility[component.id] = result.shouldShow
          } else {
            // Fallback: show component if conditional logic service is not available
            visibility[component.id] = true
          }
        } else {
          // No conditional logic means always show
          visibility[component.id] = true
        }
      }

      // Handle nested components in table cells
      if (component.cells && Array.isArray(component.cells)) {
        component.cells.forEach((row: any[]) => {
          row.forEach((cell: any) => {
            if (cell.components && Array.isArray(cell.components)) {
              evaluateComponentConditionalLogic(cell.components, visibility)
            }
          })
        })
      }

      // Handle nested components in datagrid entries
      if (component.entries && Array.isArray(component.entries)) {
        component.entries.forEach((entry: any) => {
          if (entry.components && Array.isArray(entry.components)) {
            evaluateComponentConditionalLogic(entry.components, visibility)
          }
        })
      }

      // Handle nested components in section children
      if (component.children && Array.isArray(component.children)) {
        evaluateComponentConditionalLogic(component.children, visibility)
      }
    })
  }

  // Evaluate conditional logic for all components
  const evaluateConditionalLogic = useCallback(() => {
    const visibility: Record<string, boolean> = {}
    
    if (formComponents && formComponents.length > 0) {
      evaluateComponentConditionalLogic(formComponents, visibility)
    }
    
    setComponentVisibility(visibility)
  }, [formComponents, formValues])

  // Check if a component should be visible based on conditional logic
  const shouldShowComponent = useCallback((componentId: string): boolean => {
    // Find the component to check its type
    const component = formComponents.find(comp => comp.id === componentId)
    
    // Table and datagrid components are always visible (no conditional logic)
    if (component && (component.name === 'table' || component.name === 'datagrid')) {
      return true
    }
    
    const isVisible = componentVisibility[componentId] !== false
    return isVisible
  }, [componentVisibility, formComponents])

  // Handle form value changes and re-evaluate conditional logic
  const onFormValueChange = useCallback((change: IFormControlChange) => {
    
    const newFormValues = {
      ...formValues,
      [change.id]: change.value
    }
    setFormValues(newFormValues)
    
    // Clear raised issues for this component when value changes
    // This ensures that if threshold condition changes, user must raise issue again
    const component = formComponents.find(comp => comp.id === change.id);
    if (component) {
      const threshold = (component as any)?.threshold;
      if (threshold && threshold.conditions && threshold.conditions.length > 0) {
        setRaisedThresholdIssues(prev => {
          const newSet = new Set(prev);
          threshold.conditions.forEach((condition: any) => {
            newSet.delete(condition.id);
          });
          return newSet;
        });
      }
    }
    
    // Recursive function to update component values
    const updateComponentValue = (components: FormComponentType[]): FormComponentType[] => {
      return components.map(component => {
        if (component.id === change.id) {
          if ('defaultValue' in component.basic) {
            return {
              ...component,
              basic: {
                ...component.basic,
                value: change.value ?? component.basic.defaultValue,
                defaultValue: change.value
              }
            } as FormComponentType
          }
        }
        
        // Recursively update nested components
        if (component.name === 'section' && (component as any).children) {
          return {
            ...component,
            children: updateComponentValue((component as any).children)
          } as FormComponentType
        }
        
        if (component.name === 'table' && (component as any).cells) {
          return {
            ...component,
            cells: (component as any).cells.map((row: any[]) =>
              row.map((cell: any) => {
                const updatedCell = { ...cell };
                if (updatedCell.components) {
                  updatedCell.components = updateComponentValue(updatedCell.components);
                }
                return updatedCell;
              })
            )
          } as FormComponentType
        }
        
        if (component.name === 'datagrid' && (component as any).entries) {
          return {
            ...component,
            entries: (component as any).entries.map((entry: any) => {
              const updatedEntry = { ...entry };
              if (updatedEntry.components) {
                updatedEntry.components = updateComponentValue(updatedEntry.components);
              }
              return updatedEntry;
            })
          } as FormComponentType
        }
        
        return component
      })
    }
    
    // Update form components
    const updatedComponents = updateComponentValue(formComponents)
    
    onFormDataChange?.(updatedComponents)
    
    // Clear validation errors when user starts typing
    if (validationErrors[change.id]) {
      setValidationErrors(prev => ({
        ...prev,
        [change.id]: ''
      }))
    }
    
    // Re-evaluate conditional logic with new form values
    setTimeout(() => {
      const visibility: Record<string, boolean> = {}
      
      formComponents.forEach(component => {
        // Skip conditional logic for table and datagrid components - they are always visible
        if (component.name === 'table' || component.name === 'datagrid') {
          visibility[component.id] = true
        } else if (component.conditional) {
          if (conditionalLogicService?.evaluateConditionalLogic) {
            const result = conditionalLogicService.evaluateConditionalLogic(
              component.conditional,
              formComponents as TFormComponent[],
              newFormValues
            )
            visibility[component.id] = result.shouldShow
          } else {
            // Fallback: show component if conditional logic service is not available
            visibility[component.id] = true
          }
        } else {
          visibility[component.id] = true
        }
      })
      
      setComponentVisibility(visibility)
    }, 0)
  }, [formComponents, formValues, validationErrors, onFormDataChange])

  // Handle form control blur
  const onFormControlBlur = useCallback((fieldId: string) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldId]: true
    }))
    validateField(fieldId)
  }, [])

  // Handle form control focus
  const onFormControlFocus = useCallback((fieldId: string) => {
    // Clear validation errors when field is focused
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }))
    }
  }, [validationErrors])

  // Validate individual field
  const validateField = useCallback((fieldId: string) => {
    // Only validate in test mode
    if (isPreviewMode) return
    
    const component = formComponents.find(comp => comp.id === fieldId)
    if (!component) return

    // Get current value from form values (real-time input)
    const currentValue = formValues[fieldId] || ''
    
    // Check if field has a value
    let hasValue = false
    
    if (Array.isArray(currentValue)) {
      // For array values (checkbox, multi-select), check if array has items
      hasValue = currentValue.length > 0
    } else if (typeof currentValue === 'string') {
      // For string values, check if not empty after trimming
      hasValue = currentValue.trim() !== ''
    } else {
      // For other types, check if not null/undefined
      hasValue = currentValue !== null && currentValue !== undefined
    }

    let errorMessage = ''

    // Check for required validation
    if (component.validation.required && !hasValue) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} is required`
    }
    // Check for email validation
    else if (component.name === 'email-input' && hasValue && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(currentValue)) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be a valid email`
    }
    // Check for minLength validation
    else if (component.validation.minLength && hasValue && currentValue.length < component.validation.minLength) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be at least ${component.validation.minLength} characters`
    }
    // Check for maxLength validation
    else if (component.validation.maxLength && hasValue && currentValue.length > component.validation.maxLength) {
      const customMessage = component.validation.customValidationMessage
      const fieldLabel = component.basic.label || 'This field'
      errorMessage = customMessage || `${fieldLabel} must be no more than ${component.validation.maxLength} characters`
    }
    // Check for number validations
    else if (component.name === 'number-input' && hasValue) {
      const numValue = parseFloat(currentValue)
      if (isNaN(numValue)) {
        const customMessage = component.validation.customValidationMessage
        const fieldLabel = component.basic.label || 'This field'
        errorMessage = customMessage || `${fieldLabel} must be a valid number`
      } else {
        if (component.validation.min !== undefined && numValue < component.validation.min) {
          const customMessage = component.validation.customValidationMessage
          const fieldLabel = component.basic.label || 'This field'
          errorMessage = customMessage || `${fieldLabel} must be at least ${component.validation.min}`
        } else if (component.validation.max !== undefined && numValue > component.validation.max) {
          const customMessage = component.validation.customValidationMessage
          const fieldLabel = component.basic.label || 'This field'
          errorMessage = customMessage || `${fieldLabel} must be no more than ${component.validation.max}`
        }
      }
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [fieldId]: errorMessage
    }))
  }, [formComponents, isPreviewMode, formValues])

  // Disabled automatic validation to let DfFormInput handle its own validation
  // This prevents conflicts between external and local validation
  // useEffect(() => {
  //   if (!isPreviewMode && Object.keys(formValues).length > 0) {
  //     // Validate all form components when form values change (but not on initial load)
  //     formComponents.forEach(component => {
  //       validateField(component.id)
  //     })
  //   }
  // }, [formValues, validateField, isPreviewMode, formComponents])

  // Enhanced form validity check
  const isFormValid = useCallback((): boolean => {
    return formComponents.every(component => {
      // Use real-time form values instead of component values
      const currentValue = formValues[component.id] || ''
      
      // Check if field has a value
      let hasValue = false
      
      if (Array.isArray(currentValue)) {
        // For array values (checkbox, multi-select), check if array has items
        hasValue = currentValue.length > 0
      } else if (typeof currentValue === 'string') {
        // For string values, check if not empty after trimming
        hasValue = currentValue.trim() !== ''
      } else {
        // For other types, check if not null/undefined
        hasValue = currentValue !== null && currentValue !== undefined
      }
      
      if (component.validation.required && !hasValue) {
        return false
      }
      return true
    })
  }, [formComponents, formValues])

  // Evaluate threshold condition (handles both numeric and string values)
  const evaluateThresholdCondition = useCallback((condition: any, currentVal: string | number | string[]): boolean => {
    // Handle array values (for checkbox)
    if (Array.isArray(currentVal)) {
      return currentVal.some(val => evaluateThresholdCondition(condition, val));
    }
    
    if (currentVal === undefined || currentVal === null || currentVal === '') return false;
    
    // Try numeric comparison first
    const numValue = typeof currentVal === 'string' ? parseFloat(currentVal) : currentVal;
    const thresholdValue = typeof condition.value === 'string' ? parseFloat(condition.value) : condition.value;
    
    // If both are valid numbers, use numeric comparison
    if (!isNaN(numValue) && !isNaN(thresholdValue)) {
      switch (condition.operator) {
        case 'greaterThan':
          return numValue > thresholdValue;
        case 'lessThan':
          return numValue < thresholdValue;
        case 'greaterThanOrEqual':
          return numValue >= thresholdValue;
        case 'lessThanOrEqual':
          return numValue <= thresholdValue;
        case 'equals':
          return numValue === thresholdValue;
        case 'notEquals':
          return numValue !== thresholdValue;
        default:
          return false;
      }
    }
    
    // For string comparison (checkbox, radio, select, segment)
    const stringValue = String(currentVal).toLowerCase();
    const stringConditionValue = String(condition.value).toLowerCase();
    
    switch (condition.operator) {
      case 'equals':
        return stringValue === stringConditionValue || String(currentVal) === String(condition.value);
      case 'notEquals':
        return stringValue !== stringConditionValue && String(currentVal) !== String(condition.value);
      case 'greaterThan':
      case 'lessThan':
      case 'greaterThanOrEqual':
      case 'lessThanOrEqual':
        // For string values, these operators don't make sense, return false
        return false;
      default:
        return false;
    }
  }, []);

  // Get all met threshold conditions that require issue to be raised
  const getMetThresholdConditions = useCallback((): Array<{ componentId: string; condition: any; componentLabel: string }> => {
    if (isPreviewMode) return [];
    
    const metConditions: Array<{ componentId: string; condition: any; componentLabel: string }> = [];
    
    formComponents.forEach(component => {
      const threshold = (component as any)?.threshold;
      if (!threshold || !threshold.conditions || threshold.conditions.length === 0) {
        return;
      }
      
      const currentValue = formValues[component.id];
      // Check if value is valid (not undefined, null, empty string, or empty array)
      const hasValidValue = currentValue !== undefined && 
                            currentValue !== null && 
                            currentValue !== '' && 
                            !(Array.isArray(currentValue) && currentValue.length === 0);
      
      if (!hasValidValue) {
        return;
      }
      
      threshold.conditions.forEach((condition: any) => {
        // All met threshold conditions require raising an issue (mandatory)
        if (evaluateThresholdCondition(condition, currentValue)) {
          metConditions.push({
            componentId: component.id,
            condition,
            componentLabel: component.basic?.label || 'Field'
          });
        }
      });
    });
    
    return metConditions;
  }, [formComponents, formValues, isPreviewMode, evaluateThresholdCondition]);

  // Validate threshold conditions - check if all met conditions have issues raised
  const validateThresholdConditions = useCallback((): { isValid: boolean; errorMessage?: string } => {
    if (isPreviewMode) return { isValid: true };
    
    const metConditions = getMetThresholdConditions();
    
    if (metConditions.length === 0) {
      return { isValid: true };
    }
    
    // Check if all met conditions have issues raised
    const unmetConditions = metConditions.filter(
      ({ condition }) => !raisedThresholdIssues.has(condition.id)
    );
    
    if (unmetConditions.length > 0) {
      const firstUnmet = unmetConditions[0];
      return {
        isValid: false,
        errorMessage: `Please raise an issue for the threshold condition met in "${firstUnmet.componentLabel}". You cannot submit the form until all threshold conditions have issues raised.`
      };
    }
    
    return { isValid: true };
  }, [isPreviewMode, getMetThresholdConditions, raisedThresholdIssues]);

  // Callback to mark threshold issue as raised
  const handleThresholdIssueRaised = useCallback((conditionId: string) => {
    setRaisedThresholdIssues(prev => new Set(prev).add(conditionId));
  }, []);

  // Update threshold validation state when form values or raised issues change
  useEffect(() => {
    if (!isPreviewMode) {
      const validation = validateThresholdConditions();
      setThresholdValidationState(validation);
    }
  }, [formValues, raisedThresholdIssues, isPreviewMode, validateThresholdConditions]);

  // Validate all fields
  const validateAllFields = useCallback((): boolean => {
    // Only validate in test mode
    if (isPreviewMode) return true
    
    let isValid = true
    setFormSubmitted(true)
    const errors: IFormValidationErrors = {}

    formComponents.forEach(component => {
      // Use real-time form values instead of component values
      const currentValue = formValues[component.id] || ''
      
      // Check if field has a value
      let hasValue = false
      
      if (Array.isArray(currentValue)) {
        // For array values (checkbox, multi-select), check if array has items
        hasValue = currentValue.length > 0
      } else if (typeof currentValue === 'string') {
        // For string values, check if not empty after trimming
        hasValue = currentValue.trim() !== ''
      } else {
        // For other types, check if not null/undefined
        hasValue = currentValue !== null && currentValue !== undefined
      }

      if (component.validation.required && !hasValue) {
        const customMessage = component.validation.customValidationMessage
        const fieldLabel = component.basic.label || 'This field'
        const errorMessage = customMessage || `${fieldLabel} is required`
        errors[component.id] = errorMessage
        isValid = false
      } else {
        errors[component.id] = ''
      }

      setTouchedFields(prev => ({
        ...prev,
        [component.id]: true
      }))
    })

    setValidationErrors(errors)
    return isValid
  }, [formComponents, isPreviewMode, formValues])

  // Handle form submission
  const handleSubmit = useCallback(() => {
    setFormSubmitted(true)

    if (!validateAllFields()) {
      // Focus on the first invalid field
      const firstErrorField = formComponents.find(comp => {
        const currentValue = formValues[comp.id] || ''
        
        // Check if field has a value
        let hasValue = false
        
        if (Array.isArray(currentValue)) {
          // For array values (checkbox, multi-select), check if array has items
          hasValue = currentValue.length > 0
        } else if (typeof currentValue === 'string') {
          // For string values, check if not empty after trimming
          hasValue = currentValue.trim() !== ''
        } else {
          // For other types, check if not null/undefined
          hasValue = currentValue !== null && currentValue !== undefined
        }
        
        return comp.validation.required && !hasValue
      })
      if (firstErrorField) {
        const errorElement = document.getElementById(firstErrorField.id)
        if (errorElement) {
          errorElement.focus()
        }
      }
      return
    }

    // Validate threshold conditions - ensure all met conditions have issues raised
    const thresholdValidation = validateThresholdConditions();
    if (!thresholdValidation.isValid) {
      // Show error message
      if (thresholdValidation.errorMessage) {
        if (toastService?.showError) {
          toastService.showError(thresholdValidation.errorMessage);
        } else {
          console.error('Threshold validation error:', thresholdValidation.errorMessage);
        }
      }
      // Focus on the first component with unmet threshold condition
      const metConditions = getMetThresholdConditions();
      const firstUnmet = metConditions.find(
        ({ condition }) => !raisedThresholdIssues.has(condition.id)
      );
      if (firstUnmet) {
        const errorElement = document.getElementById(firstUnmet.componentId);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    // Recursive function to update component values from form state
    const updateComponentValues = (components: FormComponentType[]): FormComponentType[] => {
      return components.map(component => {
        const userValue = formValues[component.id]
        let finalValue = userValue
        
        // Debug: Log all form values to see what's being tracked
        if (component.name === 'text-input' && component.basic?.label === 'New text input') {
        }
        
        if (finalValue === undefined) {
          if ('value' in component.basic && component.basic.value !== undefined) {
            finalValue = component.basic.value
          } else if ('defaultValue' in component.basic && component.basic.defaultValue !== undefined) {
            finalValue = component.basic.defaultValue
          } else {
            finalValue = ''
          }
        }

        // Debug table cell components
        if (component.name === 'text-input' && component.basic?.label === 'New text input') {
        }

        const updatedComponent = {
          ...component,
          basic: {
            ...component.basic,
            value: finalValue,
            defaultValue: finalValue
          }
        } as FormComponentType

        // Recursively update nested components
        if (updatedComponent.name === 'section' && (updatedComponent as any).children) {
          (updatedComponent as any).children = updateComponentValues((updatedComponent as any).children);
        }
        
        if (updatedComponent.name === 'table' && (updatedComponent as any).cells) {
          (updatedComponent as any).cells = (updatedComponent as any).cells.map((row: any[]) =>
            row.map((cell: any) => {
              const updatedCell = { ...cell };
              if (updatedCell.components) {
                updatedCell.components = updateComponentValues(updatedCell.components);
              }
              return updatedCell;
            })
          );
        }
        
        if (updatedComponent.name === 'datagrid' && (updatedComponent as any).entries) {
          (updatedComponent as any).entries = (updatedComponent as any).entries.map((entry: any) => {
            const updatedEntry = { ...entry };
            if (updatedEntry.components) {
              updatedEntry.components = updateComponentValues(updatedEntry.components);
            }
            return updatedEntry;
          });
        }

        return updatedComponent;
      });
    };

    // Prepare final form data
    const finalFormData = updateComponentValues(formComponents)

    onSubmit?.(finalFormData)
  }, [formComponents, formValues, validateAllFields, onSubmit])

  // Get preview classes
  const getPreviewClasses = useCallback(() => {
    return {
      [`device-${currentDevice}`]: true,
      readonly: isPreviewMode
    }
  }, [currentDevice, isPreviewMode])

  // Render form component based on type
  const renderFormComponent = (component: FormComponentType) => {
    const formValue = formValues[component.id]
    
    // Debug table cell components
    if (component.name === 'text-input' && component.basic?.label === 'New text input') {
    }
    
    const commonProps = {
      id: component.id,
      validationErrors,
      formValue: formValue,
      touchedFields,
      formSubmitted,
      mode: (isPreviewMode ? 'preview' : 'test') as 'preview' | 'test',
      readonly: isPreviewMode,
      onValueChange: onFormValueChange,
      onBlur: () => onFormControlBlur(component.id),
      onFocus: () => onFormControlFocus(component.id)
    }

    switch (component.name) {
      case 'text-input':
      case 'number-input':
      case 'email-input':
        // Regular text/email/number input
        return (
          <DfFormInput
            {...commonProps}
            properties={component as any}
            inputType={component.name === 'text-input' ? 'text' : 
                      component.name === 'number-input' ? 'number' : 'email'}
            formTemplateId={formTemplateId}
            onThresholdIssueRaised={handleThresholdIssueRaised}
            raisedThresholdIssues={raisedThresholdIssues}
          />
        )
      
      case 'textarea':
        return <DfFormTextarea {...commonProps} properties={component as any} />
      
      case 'select':
        // Map basic.options to top-level options for select component
        const selectProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormSelect {...commonProps} properties={selectProps as any} />
      
      case 'checkbox':
        // Map basic.options to top-level options for checkbox component
        const checkboxProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }

        return <DfFormCheckbox {...commonProps} properties={checkboxProps as any} />
      
      case 'radio':
        // Map basic.options to top-level options for radio component
        const radioProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormRadio {...commonProps} properties={radioProps as any} />
      
      case 'segment':
        // Map basic.options to top-level options for segment component
        const segmentProps = {
          ...component,
          options: (component as any).basic?.options || [],
          basic: {
            ...component.basic,
            comments: (component as any).basic?.comments || ''
          }
        }
        return <DfFormSegment {...commonProps} properties={segmentProps as any} />
      
      case 'date':
      case 'date-picker':
        return <DfFormDateTime {...commonProps} properties={component as any} />
      
      case 'datetime-picker':
        return <DfFormDateTime {...commonProps} properties={component as any} />
      
      case 'signature':
        return <DfFormSignature {...commonProps} properties={component as any} />
      
      case 'heading':
        return (
          <DfFormHeading
            {...commonProps}
            properties={component as any}
          />
        )
      
      case 'instructions':
        return (
          <DfFormInstruction
            {...commonProps}
            properties={component as any}
          />
        )
      
      case 'location':
        return <DfFormLocation {...commonProps} properties={component as any} />
      
      case 'section':
        return (
          <DfFormSection
            {...commonProps}
            properties={component as any}
            formData={formValues}
            renderFormComponent={(field: FormComponentType) => {
              // Ensure the nested component gets the proper form value
              // const fieldValue = formValues[field.id] || (field.basic as any)?.value || (field.basic as any)?.defaultValue || ''
              return renderFormComponent(field)
            }}
          />
        )
      
      case 'table':

        return (
          <React.Suspense fallback={<div>Loading table...</div>}>
            <DfFormTable 
              {...commonProps} 
              properties={component as any}
              formData={formValues}
              renderFormComponent={(field: FormComponentType) => {
                // Ensure the nested component gets the proper form value
                // const fieldValue = formValues[field.id] || (field.basic as any)?.value || (field.basic as any)?.defaultValue || ''
                return renderFormComponent(field)
              }}
            />
          </React.Suspense>
        )
      
      case 'datagrid':
        return (
          <DfFormDataGrid
            {...commonProps}
            properties={component as any}
            formData={formValues}
            onComponentSelect={onComponentSelect}
            onComponentDelete={onComponentDelete}
            onComponentEdit={onComponentEdit}
            onComponentUpdate={onComponentUpdate}
            selectedComponent={selectedComponent}
            renderFormComponent={(field: FormComponentType) => {
              // Ensure the nested component gets the proper form value
              // const fieldValue = formValues[field.id] || (field.basic as any)?.value || (field.basic as any)?.defaultValue || ''
              return renderFormComponent(field)
            }}
          />
        )
      
      case 'file':
        return <DfFormFileUpload {...commonProps} properties={component as any} />
    
      
      default:
        return (
          <div className="form-group">
            <div className="form-group-label">
              Unsupported Component: {component.name}
            </div>
          </div>
        )
    }
  }

  return (
    <div className="form-preview-container">
      <div className="form-preview-wrapper">
        <div className={`form-preview ${Object.entries(getPreviewClasses()).map(([key, value]) => value ? key : '').join(' ')}`}>
          {/* Form Canvas Header - You can implement this separately */}
          <div className="form-canvas-header">
            {/* <FormCanvasHeader 
              formData={formData}
              disableSaveDraft={false}
              disableCancel={false}
              disableSubmit={false}
              showTags={false}
            /> */}
          </div>

          {formComponents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-card">
                <div className="empty-state-preview-area">
                  <span className="empty-state-placeholder">
                    Add components to see the form preview
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <form className="form-preview-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              {/* Form Title and Description */}
              {(formTitle || formDescription) && (
                <div className="form-header">
                  {formTitle && (
                    <h1 className="form-title">{formTitle}</h1>
                  )}
                  {formDescription && (
                    <p className="form-description">{formDescription}</p>
                  )}
                </div>
              )}

              {formComponents.map((component) => {
                const basic = component.basic as any;
                const formValue = formValues[component.id];
                const isOptionBased = ['select', 'radio', 'checkbox', 'segment'].includes(component.name || '');
                const componentOptions = (component as any).options || basic?.options || [];
                
                // Get column class from component styles
                const getColumnClass = (): string => {
                  const column = (component as any)?.styles?.column;
                  // Section and table components should always be full width
                  if (component.name === 'section' || component.name === 'table' || component.name === 'datagrid') {
                    return 'col-12';
                  }
                  // Map column value to class name
                  if (column === 3) return 'col-3';
                  if (column === 6) return 'col-6';
                  if (column === 9) return 'col-9';
                  if (column === 12) return 'col-12';
                  // Default to full width if no column specified
                  return 'col-12';
                };
                
                const columnClass = getColumnClass();
                
                // Check if component or selected option has action features
                let hasActionFeatures = false;
                if (!isPreviewMode) {
                  if (isOptionBased && formValue !== undefined && formValue !== null && formValue !== '') {
                    if (component.name === 'checkbox') {
                      const selectedValues = Array.isArray(formValue) ? formValue : [formValue];
                      hasActionFeatures = selectedValues.some((value: string) => {
                        const option = componentOptions.find((opt: any) => opt.value === value);
                        return option && (option.enableNotes || option.enableAttachment || option.enableRaiseIssue || option.enableSendEmail);
                      });
                    } else {
                      const selectedOption = componentOptions.find((opt: any) => opt.value === formValue);
                      hasActionFeatures = selectedOption && (selectedOption.enableNotes || selectedOption.enableAttachment || selectedOption.enableRaiseIssue || selectedOption.enableSendEmail);
                    }
                  } else {
                    hasActionFeatures = basic?.enableNotes || basic?.enableAttachment || basic?.enableRaiseIssue || basic?.enableSendEmail;
                  }
                }
                
                return (
                  <div 
                    key={component.id} 
                    className={`form-group ${columnClass} ${hasActionFeatures ? 'has-action-features' : ''}`}
                    style={{ display: shouldShowComponent(component.id) ? 'block' : 'none' }}
                  >
                    {renderFormComponent(component)}
                    {/* Action Features - Only show in test mode */}
                    {!isPreviewMode && (
                      <ComponentActionFeatures
                        component={component as any}
                        mode={isPreviewMode ? 'preview' : 'test'}
                        formTemplateId={formTemplateId}
                        formValue={formValue}
                      />
                    )}
                  </div>
                );
              })}
              
              {!isPreviewMode && (
                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={!isFormValid() || !thresholdValidationState.isValid}
                    className="form-submit-button"
                    title={!thresholdValidationState.isValid ? thresholdValidationState.errorMessage : ''}
                  >
                    {t('formBuilder.submit', 'Submit')}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default DfFormPreview
